import { EMT_PAGE_NAMES } from '../../constants';
import { getCurrentPromoImageSrc } from '../../helpers/pps-helpers';
import { buildPagePerStoreBreadcrumbs } from '../../structured-data/build-pps-breadcrumbs';
import { buildPagePerStoreStructuredFAQ } from '../../structured-data/build-pps-structured-faq';
import { buildStoreDetailStructuredData } from '../../structured-data/build-store-detail-structured-data';

/**
 * Generates the description for the store detail page.
 *
 * @param {Object} params - Parameters for generating the description.
 * @param {string} params.name - The name of the store.
 * @param {Object} [params.address] - The address object for the store.
 * @param {string} [params.address.city] - The city where the store is located.
 * @param {string} [params.address.state] - The state where the store is located.
 * @returns {string} The generated description for the store detail page.
 */
export const generateStoreDetailDesc = ({
  name: storeName = '',
  address: {
    city = '',
    state = ''
  } = {}
} = {}) => {
  if (!city || !state || !storeName) {
    return 'The Home Depot';
  }

  return (
    'Save time on your trip to the Home Depot by scheduling your order with buy online pick up in store or '
    + `schedule a delivery directly from your ${storeName} store in ${city}, ${state}.`
  );
};

/**
 * Generates an array of preload image objects for store detail promotional images by
 * extracting image URLs from the provided topSellerMedia array and storeFinderPromotions object.
 *
 * @param {Array} topSellerMedia - An array of promotion objects for top sellers.
 * @param {Object} storeFinderPromotions - An object containing promotion data with image URLs, typically from emtData.
 * @returns {Array} An array of LinkTag objects for preloading promotional images.
 */
export const getStoreDetailPreloadImages = (topSellerMedia, storeFinderPromotions) => {
  const MAX_PRELOAD_TOP_SELLER_IMAGES = 6;
  const promotionImgUrl = getCurrentPromoImageSrc(
    storeFinderPromotions,
    EMT_PAGE_NAMES.DETAILS
  );

  const images = [];
  if (Array.isArray(topSellerMedia)) {
    topSellerMedia
      .slice(0, MAX_PRELOAD_TOP_SELLER_IMAGES)
      .forEach(({ media }) => {
        if (media?.url) {
          images.push({ href: media.url });
        }
      });
  }
  if (promotionImgUrl) {
    images.push({ href: promotionImgUrl });
  }
  return images;
};

/**
 * Generates an array of schema objects for use in generating structured data script tags
 * for the store detail page. Each schema object in the returned array will have both a
 * `content` property (the JSON-LD string) and an `id`. If either property is missing,
 * that schema object will be ignored by downstream consumers.
 *
 * If `emtSchema` is provided, it will be used as the only schema in the array.
 * Otherwise, the function will generate default schemas for breadcrumbs, local business,
 * and FAQ using the provided store and review information.
 *
 * @param {Object} params - Parameters for generating schemas.
 * @param {string} params.emtSchema - A string containing the JSON-LD schema content for the store detail page.
 * @param {Object} params.storeInfo - The store information object.
 * @param {Object} params.reviewInfo - The review information object.
 * @param {Object} params.emtContentFileData - The EMT content file data for FAQ schema generation.
 * @returns {Array} An array of schema objects, each with a `content` (string) and `id` (string).
 */
export const getStoreDetailSchemas = ({
  emtSchema,
  storeInfo,
  reviewInfo,
  emtContentFileData
}) => {
  if (emtSchema) {
    return [{ content: emtSchema, id: 'storeDetailStructuredData' }];
  }
  return ([
    {
      content: buildPagePerStoreBreadcrumbs({ storeInfo }),
      id: 'storeDetailStructuredBreadcrumb'
    },
    {
      content: buildStoreDetailStructuredData({ reviewInfo, storeInfo }),
      id: 'storeDetailStructuredLocalBusinessData'
    },
    {
      content: buildPagePerStoreStructuredFAQ(emtContentFileData),
      id: 'storeDetailStructuredFAQData'
    }
  ]);
};